import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Input,
  Tag,
  Spin,
  Empty,
  message,
  Modal,
  Form,
  Select,
  Dropdown,
  Pagination,
  Badge,
  Space,
  Checkbox,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SettingOutlined,
  DownloadOutlined,
  CheckSquareOutlined,
  CloseSquareOutlined,
  MenuOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import TopBar from '@/Components/TopBar';
import Navbar from "@/Components/navbar";
import {
  opcGetGroups,
  opcCreateGroup,
  opcUpdateGroup,
  opcDeleteGroup,
  opcBatchSetPointsGroup,
  opcGetGroupPoints,
  opcRemovePointsFromGroup,
} from '@/services/opcService';
import type { OpcGroup } from '@/services/opcService';
import BatchDownloadModal from './components/BatchDownloadModal';
import './index.less';

// ============================================================
// 假接口定义区域 —— 后续需要替换为真实 API 调用
// ============================================================

/**
 * 点位数据结构
 */
interface PointData {
  id: string;
  name: string;
  node_id: string;
  value: number | string;
  unit: string;
  status: 'Good' | 'Bad' | 'Uncertain';
  updated_at: string;
  group_id: string | null;
  major_category: string;
  sub_category: string;
  connection_id: string;
}

/**
 * 分组数据结构
 */

/**
 * 从缓存加载 browse-tree 获取的真实点位数据
 */
const loadPointsFromCache = (connectionId: string): PointData[] => {
  const cacheKey = `opc_points_${connectionId}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) return JSON.parse(cached);
  return [];
};

// ============================================================
// 常量
// ============================================================

const PAGE_SIZE_SMALL = 16;
const PAGE_SIZE_MEDIUM = 20;
const PAGE_SIZE_LARGE = 24;
const BREAKPOINT_MEDIUM = 1700;
const BREAKPOINT_LARGE = 2000;

const MAJOR_CATEGORIES = ['工艺点位', '分析仪表', '机泵', '电气', '阀门'];

const SUB_CATEGORIES: Record<string, string[]> = {
  '工艺点位': ['温度', '压力', '液位', '流量', '浓度'],
  '分析仪表': ['pH', '电导率', '浊度', 'COD', '溶解氧'],
  '机泵': ['振动', '转速', '电流', '功率', '轴承温度'],
  '电气': ['电压', '电流', '功率因数', '频率', '谐波'],
  '阀门': ['开度', '阀位反馈', '扭矩', '行程时间'],
};

const STATUS_COLORS: Record<string, string> = {
  Good: '#00B42A',
  Bad: '#F53F3F',
  Uncertain: '#FF7D00',
};

// ============================================================
// DataAccessPoint 组件
// ============================================================

const DataAccessPoint: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const connectionId = searchParams.get('connection_id') || 'default';

  // --- 加载状态 ---
  const [loading, setLoading] = useState(true);

  // --- 数据 ---
  const [allPoints, setAllPoints] = useState<PointData[]>([]);
  const [groups, setGroups] = useState<OpcGroup[]>([]);

  // --- 侧边栏 ---
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeGroupId, setActiveGroupId] = useState<string | 'all' | 'ungrouped'>('all');

  // --- 分组点位缓存（group_id → point_id[]）---
  const [groupPointIds, setGroupPointIds] = useState<Record<string, string[]>>({});

  // --- 搜索 ---
  const [searchText, setSearchText] = useState('');

  // --- 分类筛选 ---
  const [majorFilter, setMajorFilter] = useState<string | null>(null);
  const [subFilter, setSubFilter] = useState<string | null>(null);

  // --- 多选模式 ---
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // --- 分组弹窗 ---
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<OpcGroup | null>(null);
  const [groupForm] = Form.useForm();

  // --- 移动分组弹窗 ---
  const [moveModalOpen, setMoveModalOpen] = useState(false);

  // --- 批量下载弹窗 ---
  const [batchDownloadOpen, setBatchDownloadOpen] = useState(false);

  // --- 分页 ---
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    window.innerWidth >= BREAKPOINT_LARGE
      ? PAGE_SIZE_LARGE
      : window.innerWidth >= BREAKPOINT_MEDIUM
      ? PAGE_SIZE_MEDIUM
      : PAGE_SIZE_SMALL
  );

  // ==========================================================
  // 数据加载
  // ==========================================================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [points, groups] = await Promise.all([
        Promise.resolve(loadPointsFromCache(connectionId)),
        opcGetGroups(connectionId),
      ]);
      setAllPoints(points);
      setGroups(groups);
    } catch {
      message.error('加载点位数据失败');
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 响应式分页大小
  useEffect(() => {
    const handleResize = () => {
      setPageSize(
        window.innerWidth >= BREAKPOINT_LARGE
          ? PAGE_SIZE_LARGE
          : window.innerWidth >= BREAKPOINT_MEDIUM
          ? PAGE_SIZE_MEDIUM
          : PAGE_SIZE_SMALL
      );
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 分组列表加载后，批量获取所有分组的点位 ID
  useEffect(() => {
    if (groups.length === 0) return;
    const fetchAllGroupPoints = async () => {
      const results = await Promise.allSettled(
        groups.map((g) => opcGetGroupPoints(connectionId, g.id)),
      );
      const allGroupPoints: Record<string, string[]> = {};
      groups.forEach((g, i) => {
        const r = results[i];
        allGroupPoints[g.id] = r.status === 'fulfilled' ? (r.value.pointIds || []) : [];
      });
      setGroupPointIds((prev) => ({ ...allGroupPoints, ...prev }));
    };
    fetchAllGroupPoints();
  }, [groups, connectionId]);

  // 当选中特定分组时，调用 API 获取该分组包含的点位 ID 列表
  useEffect(() => {
    if (
      activeGroupId &&
      activeGroupId !== 'all' &&
      activeGroupId !== 'ungrouped' &&
      !groupPointIds[activeGroupId]
    ) {
      const fetchGroupPoints = async () => {
        try {
          const res = await opcGetGroupPoints(connectionId, activeGroupId);
          setGroupPointIds((prev) => ({
            ...prev,
            [activeGroupId]: res.pointIds || [],
          }));
        } catch {
          message.error('获取分组点位失败');
        }
      };
      fetchGroupPoints();
    }
  }, [activeGroupId, connectionId, groupPointIds]);

  // ==========================================================
  // 计算过滤后的点位
  // ==========================================================
  const filteredPoints = useMemo(() => {
    let result = [...allPoints];

    // 分组过滤
    if (activeGroupId === 'all') {
      // 显示所有
    } else if (activeGroupId === 'ungrouped') {
      // 未分组 = 不在任何自定义分组的点位
      const allGroupedIds = new Set(
        Object.values(groupPointIds).flat(),
      );
      result = result.filter((p) => !allGroupedIds.has(p.id));
    } else if (groupPointIds[activeGroupId]) {
      // 使用 API 返回的分组点位 ID 列表过滤
      const pointIdSet = new Set(groupPointIds[activeGroupId]);
      result = result.filter((p) => pointIdSet.has(p.id));
    } else {
      // API 数据尚未加载，显示空列表
      result = [];
    }

    // 搜索
    if (searchText.trim()) {
      const kw = searchText.trim().toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(kw) || p.node_id.toLowerCase().includes(kw),
      );
    }

    // 分类筛选
    if (majorFilter) {
      result = result.filter((p) => p.major_category === majorFilter);
    }
    if (subFilter) {
      result = result.filter((p) => p.sub_category === subFilter);
    }

    return result;
  }, [allPoints, activeGroupId, searchText, majorFilter, subFilter, groupPointIds]);

  // 分组统计
  const groupStats = useMemo(() => {
    // 计算所有已分组的点位 ID 集合
    const allGroupedIds = new Set(
      Object.values(groupPointIds).flat(),
    );
    const stats: Record<string, number> = {
      all: allPoints.length,
      ungrouped: allPoints.filter((p) => !allGroupedIds.has(p.id)).length,
    };
    groups.forEach((g) => {
      // 优先使用已加载的 API 数据，否则使用 point_count 作为初始值
      stats[g.id] = groupPointIds[g.id]?.length ?? g.point_count;
    });
    return stats;
  }, [allPoints, groups, groupPointIds]);

  // 分页数据
  const paginatedPoints = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPoints.slice(start, start + pageSize);
  }, [filteredPoints, currentPage, pageSize]);

  // 总页数
  const totalPages = Math.ceil(filteredPoints.length / pageSize);

  // 过滤条件变化时重置页码
  useEffect(() => {
    setCurrentPage(1);
  }, [activeGroupId, searchText, majorFilter, subFilter]);

  // ==========================================================
  // 搜索
  // ==========================================================
  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  // ==========================================================
  // 分类筛选
  // ==========================================================
  const handleMajorClick = useCallback(
    (major: string | null) => {
      if (majorFilter === major) {
        setMajorFilter(null);
        setSubFilter(null);
      } else {
        setMajorFilter(major);
        setSubFilter(null);
      }
    },
    [majorFilter],
  );

  const handleSubClick = useCallback(
    (sub: string) => {
      setSubFilter(subFilter === sub ? null : sub);
    },
    [subFilter],
  );

  // ==========================================================
  // 多选模式
  // ==========================================================
  const toggleSelectMode = useCallback(() => {
    if (selectMode) {
      setSelectedIds(new Set());
    }
    setSelectMode(!selectMode);
  }, [selectMode]);

  const togglePointSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(filteredPoints.map((p) => p.id)));
  }, [filteredPoints]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // ==========================================================
  // 分组 CRUD
  // ==========================================================
  const handleCreateGroup = useCallback(() => {
    setEditingGroup(null);
    groupForm.resetFields();
    setGroupModalOpen(true);
  }, [groupForm]);

  const handleEditGroup = useCallback(
    (group: OpcGroup) => {
      setEditingGroup(group);
      groupForm.setFieldsValue({ name: group.name, icon: group.icon });
      setGroupModalOpen(true);
    },
    [groupForm],
  );

  const handleSaveGroup = useCallback(async () => {
    try {
      const values = await groupForm.validateFields();
      if (editingGroup) {
        await opcUpdateGroup(connectionId, editingGroup.id, values);
        message.success('分组已更新');
      } else {
        await opcCreateGroup(connectionId, values);
        message.success('分组已创建');
      }
      setGroupModalOpen(false);
      loadData();
    } catch {
      // 表单验证失败
    }
  }, [groupForm, editingGroup, connectionId, loadData]);

  const handleDeleteGroup = useCallback(
    async (groupId: string) => {
      Modal.confirm({
        title: '删除分组',
        content: '确定要删除此分组吗？分组下的点位将移至"未分组"。',
        okText: '删除',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: async () => {
          await opcDeleteGroup(connectionId, groupId);
          message.success('分组已删除');
          // 清除被删除分组的缓存
          setGroupPointIds((prev) => {
            const next = { ...prev };
            delete next[groupId];
            return next;
          });
          setActiveGroupId('all');
          loadData();
        },
      });
    },
    [connectionId, loadData],
  );

  // ==========================================================
  // 批量移动分组
  // ==========================================================
  const handleBatchMove = useCallback(() => {
    if (selectedIds.size === 0) {
      message.warning('请先选择点位');
      return;
    }
    setMoveModalOpen(true);
  }, [selectedIds]);

  const handleConfirmMove = useCallback(
    async (targetGroupId: string | null) => {
      await opcBatchSetPointsGroup(connectionId, Array.from(selectedIds), targetGroupId);
      message.success(`已移动 ${selectedIds.size} 个点位`);
      setMoveModalOpen(false);
      setSelectedIds(new Set());
      setSelectMode(false);
      // 清除对应分组缓存，触发重新获取
      if (targetGroupId) {
        setGroupPointIds((prev) => {
          const next = { ...prev };
          delete next[targetGroupId];
          return next;
        });
      }
      loadData();
    },
    [connectionId, selectedIds, loadData],
  );

  // ==========================================================
  // 批量下载
  // ==========================================================
  const handleBatchDownload = useCallback(() => {
    if (selectedIds.size === 0) {
      message.warning('请先选择点位');
      return;
    }
    setBatchDownloadOpen(true);
  }, [selectedIds]);

  // ==========================================================
  // 批量从分组移除
  // ==========================================================
  const handleBatchRemoveFromGroup = useCallback(async () => {
    if (selectedIds.size === 0) {
      message.warning('请先选择点位');
      return;
    }
    if (activeGroupId === 'all' || activeGroupId === 'ungrouped') {
      message.warning('请先选择一个自定义分组');
      return;
    }
    Modal.confirm({
      title: '从分组移除',
      content: `确定要将 ${selectedIds.size} 个点位从当前分组移除吗？`,
      okText: '移除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        await opcRemovePointsFromGroup(connectionId, activeGroupId, Array.from(selectedIds));
        message.success(`已移除 ${selectedIds.size} 个点位`);
        setSelectedIds(new Set());
        setSelectMode(false);
        // 清除当前分组缓存
        setGroupPointIds((prev) => {
          const next = { ...prev };
          delete next[activeGroupId];
          return next;
        });
        loadData();
      },
    });
  }, [selectedIds, activeGroupId, connectionId, loadData]);

  // ==========================================================
  // 单个点位从分组移除
  // ==========================================================
  const handleRemovePointFromGroup = useCallback(
    async (pointId: string) => {
      if (activeGroupId === 'all' || activeGroupId === 'ungrouped') return;
      await opcRemovePointsFromGroup(connectionId, activeGroupId, [pointId]);
      message.success('已从分组移除');
      // 清除当前分组缓存
      setGroupPointIds((prev) => {
        const next = { ...prev };
        delete next[activeGroupId];
        return next;
      });
      loadData();
    },
    [activeGroupId, connectionId, loadData],
  );

  // ==========================================================
  // 返回
  // ==========================================================
  const handleBack = useCallback(() => {
    navigate('/project/home/data');
  }, [navigate]);

  // ==========================================================
  // 渲染: 侧边栏
  // ==========================================================
  const renderSidebar = () => (
    <div className={`dap-sidebar ${sidebarOpen ? 'dap-sidebar-open' : ''}`}>
      <div className="dap-sidebar-group-all">
        <div
          className={`dap-group-item ${activeGroupId === 'all' ? 'dap-active' : ''}`}
          onClick={() => setActiveGroupId('all')}
        >
          <AppstoreOutlined style={{ fontSize: 18 }} />
          <span className="dap-group-label">所有点位</span>
          <span className="dap-group-count dap-count-blue">{groupStats.all}</span>
        </div>
        <div
          className={`dap-group-item ${activeGroupId === 'ungrouped' ? 'dap-active' : ''}`}
          onClick={() => setActiveGroupId('ungrouped')}
        >
          <UnorderedListOutlined style={{ fontSize: 18 }} />
          <span className="dap-group-label">未分组</span>
          <span className="dap-group-count dap-count-gray">{groupStats.ungrouped}</span>
        </div>
      </div>

      <div className="dap-sidebar-divider" />

      <div className="dap-sidebar-groups">
        <div className="dap-sidebar-header">
          <span>自定义分组</span>
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={handleCreateGroup}
          />
        </div>
        {groups.map((group) => (
          <div
            key={group.id}
            className={`dap-group-item ${activeGroupId === group.id ? 'dap-active' : ''}`}
            onClick={() => setActiveGroupId(group.id)}
          >
            <FolderOutlined
              style={{ fontSize: 18, color: '#ffb53b' }}
            />
            <span className="dap-group-label">{group.name}</span>
            <span className="dap-group-count dap-count-gray">{groupStats[group.id] || 0}</span>
            <Dropdown
              menu={{
                items: [
                  { key: 'edit', label: '编辑', icon: <EditOutlined />, onClick: () => handleEditGroup(group) },
                  { key: 'delete', label: '删除', icon: <DeleteOutlined />, danger: true, onClick: () => handleDeleteGroup(group.id) },
                ],
              }}
              trigger={['contextMenu']}
            >
              <Button
                type="text"
                size="small"
                icon={<SettingOutlined />}
                className="dap-group-settings"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditGroup(group);
                }}
              />
            </Dropdown>
          </div>
        ))}
      </div>
    </div>
  );

  // ==========================================================
  // 渲染: 分类筛选栏
  // ==========================================================
  const renderCategoryFilter = () => (
    <div className="dap-category-filter">
      <div className="dap-filter-row">
        <Tag
          className={`dap-filter-tag ${!majorFilter ? 'dap-filter-active' : ''}`}
          onClick={() => { setMajorFilter(null); setSubFilter(null); }}
        >
          全部
        </Tag>
        {MAJOR_CATEGORIES.map((major) => (
          <Tag
            key={major}
            className={`dap-filter-tag ${majorFilter === major ? 'dap-filter-active' : ''}`}
            onClick={() => handleMajorClick(major)}
          >
            {major}
          </Tag>
        ))}
      </div>
      {majorFilter && subFilter && (
        <div className="dap-filter-row dap-filter-sub">
          {SUB_CATEGORIES[majorFilter]?.map((sub) => (
            <Tag
              key={sub}
              className={`dap-filter-tag dap-filter-sub-tag ${subFilter === sub ? 'dap-filter-active' : ''}`}
              onClick={() => handleSubClick(sub)}
            >
              {sub}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );

  // ==========================================================
  // 渲染: 点位卡片
  // ==========================================================
  const renderPointCard = (point: PointData) => {
    console.log('point', point);
    const isSelected = selectMode && selectedIds.has(point.id);

    const handleCardClick = () => {
      if (selectMode) {
        // 选择模式下点击卡片不处理，只通过 Checkbox 选择
        return;
      }
      // 跳转到点位详情页
      navigate(`/project/home/data/point/info?point_id=${point.id}&connection_id=${connectionId}`);
    };
    console.log('paginatedPoints', paginatedPoints);

    return (
      <Card
        key={point.id}
        className={`dap-point-card ${isSelected ? 'dap-card-selected' : ''}`}
        hoverable
        onClick={handleCardClick}
      >
        {selectMode && (
          <Checkbox
            checked={isSelected}
            className="dap-card-checkbox"
            onClick={(e) => {
              e.stopPropagation();
              togglePointSelection(point.id);
            }}
          />
        )}
        <div className="dap-card-header">
          <div className="dap-card-name" title={point.name}>
            {point.name}
          </div>
          <Badge
            status={
              point.status === 'Good'
                ? 'success'
                : point.status === 'Bad'
                  ? 'error'
                  : 'warning'
            }
            text={
              <span style={{ color: STATUS_COLORS[point.status], fontSize: 14 }}>
                {point.status}
              </span>
            }
          />
        </div>
        <div className="dap-card-id" title={point.node_id}>
          {point.node_id}
        </div>

        <div className="dap-card-value">
          {(() => {
            const pointGroups = Object.entries(groupPointIds)
              .filter(([, ids]) => ids.includes(point.id))
              .map(([groupId]) => groups.find((g) => g.id === groupId)?.name)
              .filter(Boolean);
            return pointGroups.length > 0
              ? pointGroups.map((name) => (
                  <span key={name} className="dap-group-tag">{name}</span>
                ))
              : <span className="dap-group-none">未分组</span>;
          })()}
        </div>
        <div className="dap-card-time">
          {/* {new Date(point.updated_at).toLocaleTimeString('zh-CN')} */}
          {dayjs(point.updated_at).format("YYYY-MM-DD HH:mm:ss")}
        </div>
        {!selectMode && activeGroupId !== 'all' && activeGroupId !== 'ungrouped' && (
          <Button
            type="text"
            size="small"
            danger
            icon={<MinusCircleOutlined />}
            className="dap-card-remove-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleRemovePointFromGroup(point.id);
            }}
          >
            移出分组
          </Button>
        )}
      </Card>
    );
  };

  // ==========================================================
  // 主渲染
  // ==========================================================
  return (
    <>
      <TopBar title="工艺设计" mainContent={<Navbar /> } />
      
      <div className="dap-page">
        {/* 左侧边栏 */}
        <button
          className={`dap-sidebar-toggle ${sidebarOpen ? 'dap-sidebar-toggle-open' : ''}`}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <MenuOutlined />
        </button>
        {renderSidebar()}

        {/* 主内容 */}
        <div className="dap-main">
          {/* 顶部工具栏 */}
          <div className="dap-toolbar">
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              className="dap-back-btn"
            >
              返回连接管理
            </Button>
            <div className="dap-toolbar-right">
              <Space size={8}>
                <Input.Search
                  placeholder="搜索点位名称或ID"
                  allowClear
                  onSearch={handleSearch}
                  onChange={(e) => { if (!e.target.value) setSearchText(''); }}
                  style={{ width: 240 }}
                  prefix={<SearchOutlined style={{ color: '#C9CDD4' }} />}
                />
                <Button
                  icon={selectMode ? <CloseSquareOutlined /> : <CheckSquareOutlined />}
                  onClick={toggleSelectMode}
                >
                  {selectMode ? '退出选择' : '批量操作'}
                </Button>
                {selectMode && (
                  <>
                    <Button size="small" onClick={selectAll}>全选</Button>
                    <Button size="small" onClick={deselectAll}>取消全选</Button>
                  </>
                )}
              </Space>
            </div>
          </div>

          {/* 分类筛选 */}
          {renderCategoryFilter()}

          {/* 点位列表面板 */}
          <div className="dap-content">
            <Spin spinning={loading}>
              {filteredPoints.length === 0 ? (
                <Empty
                  image={<ThunderboltOutlined style={{ fontSize: 64, color: '#C9CDD4' }} />}
                  description="暂无点位数据"
                />
              ) : (
                <>
                  <div className="dap-points-grid">
                    {paginatedPoints.map(renderPointCard)}
                  </div>

                  {totalPages > 1 && (
                    <div className="dap-pagination">
                      <Pagination
                        current={currentPage}
                        total={filteredPoints.length}
                        pageSize={pageSize}
                        onChange={setCurrentPage}
                        showSizeChanger={false}
                        showTotal={(total) => `共 ${total} 个点位`}
                      />
                    </div>
                  )}
                </>
              )}
            </Spin>
          </div>

          {/* 底部批量操作栏 */}
          {selectMode && (
            <div className="dap-batch-bar">
              <span className="dap-batch-count">已选 {selectedIds.size} 个点位</span>
              <Space>
                <Button
                  icon={<FolderOpenOutlined />}
                  onClick={handleBatchMove}
                  disabled={selectedIds.size === 0}
                >
                  移动到分组
                </Button>
                {activeGroupId !== 'all' && activeGroupId !== 'ungrouped' && (
                  <Button
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={handleBatchRemoveFromGroup}
                    disabled={selectedIds.size === 0}
                  >
                    从分组移除
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleBatchDownload}
                  disabled={selectedIds.size === 0}
                >
                  批量下载数据
                </Button>
              </Space>
            </div>
          )}
        </div>

        {/* 分组编辑弹窗 */}
        <Modal
          title={editingGroup ? '编辑分组' : '新建分组'}
          open={groupModalOpen}
          onOk={handleSaveGroup}
          onCancel={() => setGroupModalOpen(false)}
          okText="保存"
          cancelText="取消"
          destroyOnClose
        >
          <Form form={groupForm} layout="vertical">
            <Form.Item
              label="分组名称"
              name="name"
              rules={[{ required: true, message: '请输入分组名称' }]}
            >
              <Input placeholder="例如：换热单元" maxLength={20} />
            </Form.Item>
          </Form>
        </Modal>

        {/* 移动到分组弹窗 */}
        <Modal
          title="移动到分组"
          open={moveModalOpen}
          onCancel={() => setMoveModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <div className="dap-move-list">
            {groups.map((group) => (
              <div
                key={group.id}
                className="dap-move-item"
                onClick={() => handleConfirmMove(group.id)}
              >
                <FolderOutlined style={{ fontSize: 18, color: '#ffb53b' }} />
                <span>{group.name}</span>
              </div>
            ))}
          </div>
        </Modal>

        {/* 批量下载弹窗 */}
        <BatchDownloadModal
          open={batchDownloadOpen}
          onClose={() => {
            setBatchDownloadOpen(false);
            setSelectedIds(new Set());
            setSelectMode(false);
          }}
          selectedPoints={allPoints.filter((p) => selectedIds.has(p.id))}
          connectionId={connectionId}
        />
      </div>
    </>
  );
};

export default DataAccessPoint;
